import React from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import { DEBUG_MODE } from "@utils/config";
import ErrorPage from "./ErrorPage";
import {
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
  refreshAccessToken,
  clearAuth,
  saveRedirectUrl,
} from "@utils/authService";

// Max time to wait on a refresh-token network call before giving up and
// redirecting to login. Guarantees the UI never hangs indefinitely.
const REFRESH_CHECK_TIMEOUT_MS = 8000;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    // status: "ok" | "error" | "pending"
    // "pending" renders nothing while we verify (or act on) an expired
    // session, so the Oops page never flashes before a login redirect.
    this.state = { status: "ok" };
  }

  static getDerivedStateFromError(error) {
    if (DEBUG_MODE) {
      // eslint-disable-next-line no-console
      console.error("Error: an error occurred in component tree", error);
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    // No tokens at all, or access token expired with no refresh token to
    // recover with -> definitely redirecting to login, never show Oops.
    if (!accessToken && !refreshToken) {
      return { status: "pending" };
    }
    if (accessToken && !isAccessTokenExpired(accessToken)) {
      // Access token still valid -> unrelated to auth, show Oops.
      return { status: "error" };
    }
    if (!refreshToken) {
      return { status: "pending" };
    }

    // Access token expired but a refresh token exists: outcome depends on
    // a network call, resolved in componentDidCatch. Render nothing until
    // that resolves so Oops never flashes before a possible redirect.
    return { status: "pending" };
  }

  componentDidCatch(error, errorInfo) {
    if (DEBUG_MODE) {
      // eslint-disable-next-line no-console
      console.error("Error: an error occurred in component tree", error);
      // eslint-disable-next-line no-console
      console.error("Error INFO: error details", errorInfo);
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    // No tokens at all -> session is gone, go straight to login.
    if (!accessToken && !refreshToken) {
      saveRedirectUrl();
      clearAuth();
      window.location.href = "/login";
      return;
    }

    // Access token is still valid -> already rendering Oops (see
    // getDerivedStateFromError), nothing more to do.
    if (accessToken && !isAccessTokenExpired(accessToken)) {
      return;
    }

    // Access token is expired. If there's no refresh token to recover
    // with, go straight to login.
    if (!refreshToken) {
      saveRedirectUrl();
      clearAuth();
      window.location.href = "/login";
      return;
    }

    // Access token is expired but a refresh token exists: verify it's
    // still valid. Bounded by a timeout so the UI can never hang
    // indefinitely waiting on the network.
    const timeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error("refresh check timed out")), REFRESH_CHECK_TIMEOUT_MS);
    });

    // refreshAccessToken() is single-flight and shared with the axios
    // interceptor, so if a refresh is already in progress (e.g. triggered
    // by the failed API call that caused this render error) this joins
    // the same promise instead of firing a duplicate/racing request.
    Promise.race([refreshAccessToken(), timeout])
      .then(() => {
        // Refresh succeeded: session is valid, this wasn't an auth issue.
        this.setState({ status: "error" });
      })
      .catch(() => {
        // Both access and refresh tokens are expired/invalid (or the check
        // timed out) -> redirect directly to login without ever showing
        // the Oops page. refreshAccessToken() already clears auth
        // storage on failure.
        window.location.href = "/login";
      });
  }

  render() {
    const { status } = this.state;
    const { children, showHomeButton } = this.props;
    if (status === "pending") {
      return null;
    }
    if (status === "error") {
      // fallback UI
      return (
        <ErrorPage
          code="500"
          title="GENERIC_SOMETHING_WENT_WRONG_TITLE"
          message="GENERIC_SOMETHING_WENT_WRONG_MESSAGE"
          showHomeButton={showHomeButton}
        />
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  t: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  showHomeButton: PropTypes.bool,
};

ErrorBoundary.defaultProps = {
  showHomeButton: true,
};

export default withTranslation()(ErrorBoundary);
